import defaultsDeep from 'lodash/defaultsDeep';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { createErrorAnalytics } from '@atlassian/jira-polaris-lib-errors/src/controllers/index.tsx';
import { maybeWaitForPremiumModal } from '../utils.tsx';
import type { Props, State, ViewCreatorTourSteps } from '../../types.tsx';
import { VIEW_CREATOR_TOUR_SPOTLIGHTS } from '../../constants.tsx';
import { normalizeError } from '../../../common/utils.tsx';

type RemoteState = {
	tourSpotlightStep: ViewCreatorTourSteps | null;
	tourDismissed: boolean;
};

export const loadViewCreatorSpotlightState =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		if (fg('polaris_premium_modal_ga')) {
			await maybeWaitForPremiumModal(getState);
		}

		// User property is already loaded
		if (typeof getState().viewCreator.tourDismissed === 'boolean') {
			return;
		}

		try {
			const remoteState = await getUserProperty<RemoteState>(
				accountId,
				VIEW_CREATOR_TOUR_SPOTLIGHTS,
			);

			const state = getState();

			setState({
				...state,
				viewCreator: {
					...state.viewCreator,
					...defaultsDeep(remoteState, {
						tourSpotlightStep: null,
						tourDismissed: false,
					}),
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					...state,
					viewCreator: {
						...state.viewCreator,
						tourDismissed: false,
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.view-creator-spotlight',
				`Failed to get user property with key: ${VIEW_CREATOR_TOUR_SPOTLIGHTS}`,
				error instanceof Error ? error : new Error(String(error)),
			);
		}
	};

export const setViewCreatorSpotlightTourStep =
	(step: ViewCreatorTourSteps | null): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();
		const newViewCreatorState = {
			...state.viewCreator,
			tourSpotlightStep: step,
		};

		setState({
			...state,
			viewCreator: newViewCreatorState,
		});

		try {
			await setUserProperties(
				accountId,
				VIEW_CREATOR_TOUR_SPOTLIGHTS,
				JSON.stringify(newViewCreatorState),
			);
		} catch (error) {
			fireErrorAnalytics(
				createErrorAnalytics('polaris.onboarding.view-creator-spotlight', normalizeError(error)),
			);
		}
	};

export const setViewCreatorSpotlightDismissed =
	(tourDismissed = true): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();
		const newViewCreatorState = {
			...state.viewCreator,
			tourDismissed,
		};

		setState({
			...state,
			viewCreator: newViewCreatorState,
		});

		try {
			await setUserProperties(
				accountId,
				VIEW_CREATOR_TOUR_SPOTLIGHTS,
				JSON.stringify(newViewCreatorState),
			);
		} catch (error) {
			fireErrorAnalytics(
				createErrorAnalytics('polaris.onboarding.view-creator-spotlight', normalizeError(error)),
			);
		}
	};
